<template>
  <section class="flex flex-col md:flex-row w-full px-4 md:px-0">
    <!-- SIDEBAR -->
    <div class="hidden md:block w-96 flex-shrink-0">
      <div class="sidecard-base h-auto lg:h-72 bg-white">
        <div class="flex gap-2 items-center pt-2">
          <HelpSvg />
          <h1 class="text-lg sm:text-xl lg:text-2xl font-semibold text-primarydark">NEED HELP?</h1>
        </div>

        <div class="py-4 sm:py-6 space-y-3 text-sm">
          <h3 class="text-sm sm:text-base font-medium">Filter</h3>

          <select v-model="selectedTag" class="mt-2 p-2 border rounded w-full capitalize text-sm">
            <option disabled value="">-- Select Tags --</option>
            <option value="">All</option>
            <option v-for="item in itemTags" :key="item.id" :value="item.id">
              {{ item.name }}
            </option>
          </select>
        </div>
      </div>
    </div>
    <div class="block md:hidden sm:py-6 pb-5 text-sm">
      <h3 class="text-sm sm:text-base font-medium">Filter</h3>

      <select v-model="selectedTag" class="mt-2 p-2 border rounded w-full capitalize text-sm">
        <option disabled value="">-- Select Tags --</option>
        <option value="">All</option>
        <option v-for="item in itemTags" :key="item.id" :value="item.id">
          {{ item.name }}
        </option>
      </select>
    </div>
    <!-- MAIN CONTENT -->
    <div class="flex-1 md:pl-6">
      <!-- INBOX VIEW -->
      <div v-if="isUnReadInbox">
        <!-- PINNED -->
        <div class="flex gap-2 items-center">
          <h3 class="text-sm sm:text-base font-medium">PINNED ANNOUNCEMENT</h3>
          <PinnedSvg />
        </div>

        <div class="py-4">
          <ul
            v-if="passSectionItemPost.length"
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            <li
              v-for="(post, index) in renderByPinned"
              :key="index"
              @click="renderPostHandler(post)"
              class="bg-white rounded-sm p-4 cursor-pointer min-h-[120px]"
            >
              <h3 class="text-primaryPaletteLight text-base sm:text-lg font-semibold mb-2">
                {{ post.title }}
              </h3>

              <div class="flex items-center text-primarydark text-xs sm:text-sm">
                <span class="mr-2" v-if="post.tags.length">
                  <TagSvg />
                </span>

                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="tag in post.tags"
                    :key="tag.id"
                    class="px-2 py-1 text-xs bg-orange-50 rounded"
                  >
                    {{ tag.name }}
                  </span>
                </div>
              </div>
            </li>
          </ul>

          <span v-else class="text-primarylightdark text-sm">
            There are no pinned announcements
          </span>
        </div>

        <!-- GROUPED POSTS -->
        <div class="py-4">
          <div v-if="Object.keys(groupedByDate).length">
            <div v-for="(posts, date) in groupedByDate" :key="date" class="mb-6">
              <h2 class="text-gray-700 font-bold uppercase mb-4 text-sm sm:text-base">
                {{ renderDateFormat(date) }}
              </h2>

              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div
                  v-for="post in posts"
                  :key="post.id"
                  @click="renderPostHandler(post)"
                  class="bg-white rounded-sm p-4 cursor-pointer min-h-[120px]"
                >
                  <h3 class="text-primaryPaletteLight text-base sm:text-lg font-semibold mb-2">
                    {{ post.title }}
                  </h3>

                  <div class="flex items-center text-primarydark text-xs sm:text-sm">
                    <span class="mr-2" v-if="post.tags.length">
                      <TagSvg />
                    </span>

                    <div class="flex flex-wrap gap-1">
                      <span
                        v-for="tag in post.tags"
                        :key="tag.id"
                        class="px-2 py-1 text-xs bg-orange-50 rounded"
                      >
                        {{ tag.name }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <span v-else class="text-primarylightdark text-sm">
            There are currently no announcements
          </span>
        </div>
      </div>

      <!-- DETAILS VIEW -->
      <div v-else class="relative">
        <div class="mb-4 lg:absolute lg:right-0 lg:-top-12">
          <button
            class="flex items-center gap-2 text-sm font-medium bg-primarydark text-white py-2 px-4 rounded-sm"
            @click="isUnReadInbox = !isUnReadInbox"
          >
            <BackSvg class="w-4 h-4" />
            Back
          </button>
        </div>

        <div class="content-base">
          <h1
            class="pt-4 pb-2 text-xl sm:text-2xl lg:text-3xl font-semibold text-primaryPaletteLight"
          >
            {{ itemSectionPostDetails.title }}
          </h1>

          <div class="flex gap-2 items-center text-primarydark text-xs sm:text-sm">
            <CalendarSvg />
            <span>
              {{ renderDateFormat(itemSectionPostDetails.createdAt) }}
            </span>
          </div>

          <div
            class="prose break-words py-6 leading-7 text-sm sm:text-base text-primarydark max-w-full"
            v-html="sanitizeHtml(itemSectionPostDetails.content)"
          ></div>
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import encryptHandler from '@/utils/encrypt';
import HelpSvg from '@/components/svgs/HelpSvg.vue';
import PinnedSvg from '@/components/svgs/PinnedSvg.vue';
import CalendarSvg from '@/components/svgs/CalendarSvg.vue';
import BackSvg from '@/components/svgs/BackSvg.vue';
import TagSvg from '@/components/svgs/TagSvg.vue';
import reusableBase from '@/utils/reusables/dateFormat';
import DOMPurify from 'dompurify';
import { computed, inject, onMounted, ref } from 'vue';

export default {
  components: {
    HelpSvg,
    PinnedSvg,
    BackSvg,
    CalendarSvg,
    TagSvg
  },
  props: {
    passSectionItemPost: Object,
    passSectionId: Number
  },
  setup(props) {
    const axios = inject('$axios');
    const { encryptFile } = encryptHandler();
    const { renderDateFormat } = reusableBase();

    const itemSectionPost = computed(() => props.passSectionItemPost);

    const selectedTag = ref('');
    const renderCurrentLoading = ref(true);
    const isUnReadInbox = ref(true);
    const itemSectionPostDetails = ref([]);
    const selectedPostID = ref();
    const itemTags = ref([]);

    const groupedByDate = computed(() => {
      return [...itemSectionPost.value]
        .filter((item) => {
          if (item.isPinned) return false;
          if (!selectedTag.value) return true;

          // ✅ FIX: accurate tag filtering
          return item.tags?.some((tag) => tag.id === Number(selectedTag.value));
        })
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .reduce((acc, item) => {
          const date = item.createdAt.split('T')[0];
          (acc[date] ||= []).push(item);
          return acc;
        }, {});
    });

    const renderByPinned = [...itemSectionPost.value]
      .filter((item) => item.isPinned)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const renderPostHandler = (item) => {
      isUnReadInbox.value = !isUnReadInbox.value;
      itemSectionPostDetails.value = item;
      selectedPostID.value = item.id;
      tagAsReadPostHandler();
    };

    onMounted(() => {
      fetchItemTags();
    });

    const fetchItemTags = async () => {
      try {
        const result = await axios.get(`/api/module-tags/module-section/${props.passSectionId}`, {
          params: {
            includeSection: false,
            includeModule: false
          }
        });

        if (result.data) {
          renderCurrentLoading.value = false;
          itemTags.value = result.data.data;
        }
      } catch (error) {
        console.log('render', error);
      }
    };
    const tagAsReadPostHandler = async () => {
      try {
        console.log('hello');
        const result = await axios.put(
          `/api/push-notifications/mark-read/post/${selectedPostID.value}`
        );

        console.log('read', result);
      } catch (error) {
        console.log('render', error);
      }
    };
    const sanitizeHtml = (html) => {
      if (!html) return '';

      // Remove &nbsp;
      html = html.replace(/&nbsp;/g, ' ');

      // Remove multiple spaces
      html = html.replace(/\s{2,}/g, ' ');

      // Remove empty paragraphs like <p><br></p>
      html = html.replace(/<p>(\s|<br\s*\/?>)*<\/p>/gi, '');

      // Trim leading & trailing spaces
      html = html.trim();

      return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
    };
    return {
      itemSectionPost,
      groupedByDate,
      renderDateFormat,
      encryptFile,
      isUnReadInbox,
      renderPostHandler,
      itemSectionPostDetails,
      renderByPinned,
      fetchItemTags,
      itemTags,
      renderCurrentLoading,
      selectedTag,
      tagAsReadPostHandler,
      selectedPostID,

      sanitizeHtml
    };
  }
};
</script>
